import { resolveAmputationType, showsAmputationTypeField } from '@/constants/order/amputationLevel';
import { AmputationType } from '@/constants/order/amputationType';
import { ImageSetModel } from '@/store/models/Image';
import { OrderModel } from '@/store/models/Order';
import { hasUsableImages } from '@/tools/images';

/**
 * Montaż protezy po wyłuszczeniu w stawie barkowym (GLA-75).
 *
 * Acro to **lej protezowy** — podstawa protezy. Na niego idzie drukowane ramię (INNE niż
 * „Glaze Arm" z protez transhumeralnych), a dopiero na to łokieć i przedramię wybierane
 * w dalszych krokach zamówienia.
 *
 * Markery `_sync_id` nadaje migracja katalogu i są jedynym stabilnym identyfikatorem tych
 * części: slug i nazwę panel administracyjny zmienia jednym ruchem.
 */
export const ACRO_CAP_SYNC_ID = 'gla64-acro-cap';
export const ACRO_ARM_SYNC_ID = 'gla64-acro-arm';

export interface AcroAssemblyItem {
	id: number;
	sync_id: string | null;
	name: string;
	slug: string;
	excerpt: string | null;
	sku_group: string | null;
	sku_type: string | null;
	/** `null` = część bazowa (siada na pacjencie); inaczej `sync_id` części, na której siada. */
	mounts_to: string | null;
	image_uri: Partial<ImageSetModel>;
}

/**
 * Grafiki części montażu dostarczone przez Aleksa (GLA-66, 389 / 778 / 1167 px).
 *
 * Katalog `arms` nie ma ich u siebie, bo wgrywa się je w panelu administracyjnym, a przed
 * targami nikt tego nie zrobił. Dlatego front ma FALLBACK po markerze `_sync_id` —
 * z pierwszeństwem dla katalogu: gdy ktoś wgra grafikę w panelu, endpoint ją poda i to ona
 * wygrywa. Pliki leżą w `ui/public/img/acro/`, konwencją `@/constants/hands/constants.ts`.
 *
 * Nazwy plików są nazwami ASSETÓW od Aleksa i nie muszą trzymać się slugów z katalogu
 * (migracja przemianowała kapę na `glaze-acro-set`). Dopasowanie idzie po `_sync_id`, więc
 * rozjazd nazwy niczego nie psuje — nie szukaj tu pliku po slugu produktu.
 */
const ASSEMBLY_IMAGE_FALLBACK: Record<string, ImageSetModel> = {
	[ACRO_CAP_SYNC_ID]: {
		image_1x: '/img/acro/glaze-acro-with-a-lex.png',
		image_2x: '/img/acro/glaze-acro-with-a-lex@2x.png',
		image_3x: '/img/acro/glaze-acro-with-a-lex@3x.png',
	},
	[ACRO_ARM_SYNC_ID]: {
		image_1x: '/img/acro/3d-printed-arm-socket.png',
		image_2x: '/img/acro/3d-printed-arm-socket@2x.png',
		image_3x: '/img/acro/3d-printed-arm-socket@3x.png',
	},
};

export interface AcroAssembly {
	data: AcroAssemblyItem[];
	/** Pary SKU, których nie ma w katalogu. Niepusta = komplet jest NIEPEŁNY. */
	missing_sku_pairs: string[];
}

/**
 * Etapy ekranu zakładania zamówienia. Środkowy pokazuje kapę barkową i pojawia się WYŁĄCZNIE
 * w ścieżce barkowej — pozostałe kafle idą z etapu kafli prosto na listę produktów.
 *
 * RAMIENIA DO KAPY TU NIE MA (GLA-80). Stało tuż po kapie, a ekran wyboru leja i jego
 * wykończenia jest kilka kroków dalej — więc protetyk oglądał ramię w jednym miejscu, a jego
 * wykończenie w zupełnie innym. Teraz ramię pokazuje się w kroku Socket Details, tuż przed
 * wyborem wykończenia, spójnie z pozostałymi ścieżkami (decyzja Alicji, 07.10).
 */
export enum OrderNewStage {
	Tiles = 1,
	AcroCap = 2,
	Products = 3,
}

export function requiresAcroAssembly(amputationType?: AmputationType): boolean {
	return amputationType === AmputationType.ShoulderDisarticulation;
}

/**
 * Etap, na który wchodzimy po wybraniu kafla.
 */
export function stageAfterTile(amputationType?: AmputationType): OrderNewStage {
	return requiresAcroAssembly(amputationType) ? OrderNewStage.AcroCap : OrderNewStage.Products;
}

export function isAssemblyComplete(assembly: AcroAssembly | null): boolean {
	if (!assembly) {
		return false;
	}

	return (
		assembly.missing_sku_pairs.length === 0 &&
		!!findBySyncId(assembly, ACRO_CAP_SYNC_ID) &&
		!!findBySyncId(assembly, ACRO_ARM_SYNC_ID)
	);
}

export function findBySyncId(
	assembly: AcroAssembly | null,
	syncId: string,
): AcroAssemblyItem | null {
	return assembly?.data.find(item => item.sync_id === syncId) ?? null;
}

/**
 * Pozycje montażu dla jednoelementowej listy kafli (`ProductList` oczekuje tablicy).
 */
export function tileListFor(item: AcroAssemblyItem | null): AcroAssemblyItem[] {
	return item ? [withFallbackImage(item)] : [];
}

/**
 * Uzupełnia grafikę z frontu, gdy katalog jej nie ma. Katalog ma PIERWSZEŃSTWO — wgranie
 * grafiki w panelu nadpisuje fallback bez zmiany kodu.
 *
 * Zestaw NIEPEŁNY (np. sam `1x` wgrany w panelu) traktujemy jak brak i zastępujemy w CAŁOŚCI,
 * bo mieszanka gęstości daje pustą ramkę na ekranach retina. Skutek uboczny: admin, który wgra
 * jedną gęstość, zobaczy grafikę z repo i może uznać, że panel nie działa — świadomy wybór,
 * bo pusta ramka jest gorsza.
 */
export function withFallbackImage(item: AcroAssemblyItem): AcroAssemblyItem {
	if (hasUsableImages(item.image_uri as ImageSetModel)) {
		return item;
	}

	const fallback = item.sync_id ? ASSEMBLY_IMAGE_FALLBACK[item.sync_id] : undefined;

	return fallback ? { ...item, image_uri: fallback } : item;
}

/**
 * Czy zamówienie POWSTAŁO ścieżką barkową — decyduje kafel, nie zapisany wybór protetyka.
 *
 * Rozłączne z `isShoulderDisarticulationOrder`, które rozstrzyga po wartości ROZWIĄZANEJ
 * (zapisany wybór wygrywa z kaflem). Tamto jest właściwe tam, gdzie pytamy „czym to zamówienie
 * JEST teraz"; to jest właściwe tam, gdzie pytamy „którą ścieżką protetyk tu wszedł".
 *
 * Rozróżnienie nie jest teoretyczne: widoczność pola typu amputacji liczona z wartości
 * rozwiązanej robiła PUŁAPKĘ JEDNOKIERUNKOWĄ. Zamówienie założone z kafla Transhumeral, w którym
 * protetyk przez omyłkę wybrał „Shoulder disarticulation" i zapisał, traciło to pole z ekranu
 * i nie dawało się już cofnąć (znalazła Rita, GLA-81).
 */
export function isShoulderDisarticulationPath(order?: OrderModel | null): boolean {
	return (
		order?.prosthesis?.prosthesis_category?.slug === 'glaze-above'
		&& order?.initial_amputation_type === AmputationType.ShoulderDisarticulation
	);
}

/**
 * Czy to zamówienie idzie ścieżką barkową (Glaze Acro).
 *
 * BRAMKA NA KATEGORIĘ jest konieczna, nie ozdobna: `amputation_type` bywa NIEWYZEROWANY na
 * zamówieniu Below, gdy protetyk zmienił produkt po wypełnieniu szczegółów. Bez niej takie
 * zamówienie traciłoby kroki, których jego ścieżka wymaga. Ten sam stan danych raz już
 * wyprodukował błędną kartę PDF (`OrderPreviewViewModel`).
 */
export function isShoulderDisarticulationOrder(order?: OrderModel | null): boolean {
	if (order?.prosthesis?.prosthesis_category?.slug !== 'glaze-above') {
		return false;
	}

	return (
		resolveAmputationType(order?.order_details?.amputation_type, order?.initial_amputation_type) ===
		AmputationType.ShoulderDisarticulation
	);
}

/**
 * Kroki po potwierdzeniu zamówienia. Ścieżka barkowa NIE ma leja testowego, więc nie wolno jej
 * kazać czekać na check socket ani go potwierdzać — to kroki, których w niej nie ma (GLA-64
 * krok 8, zgłoszone przez Alicję w GLA-75).
 */
export const AFTER_CONFIRMATION_STEPS = [
	'Please connect your scan. Align it and choose the print option.',
	'Wait for the check socket.',
	'Meet with your patient and try the check socket. Heat it and make changes if necessary. If you made any changes please scan the socket on the patient.',
	'After visit log into I’AM App and confirm the check socket. Make notes and connect new scan.',
	'Wait for your Arm In The Box!',
];

export const AFTER_CONFIRMATION_STEPS_SHOULDER = [
	'Please connect your scan.',
	'Wait for your Arm In The Box!',
];

export function afterConfirmationSteps(order?: OrderModel | null): string[] {
	return isShoulderDisarticulationOrder(order)
		? AFTER_CONFIRMATION_STEPS_SHOULDER
		: AFTER_CONFIRMATION_STEPS;
}

/**
 * Czyta odpowiedź endpointu `/acro-assembly`.
 *
 * DLACZEGO OSOBNA FUNKCJA: `api.get` rozpakowuje już body, więc sięgnięcie po `.data`
 * przypisywało samą tablicę części i blokowało ekran kapy — a tego czyste testy funkcji nie
 * widziały, bo błąd siedział w jednej linii komponentu (znalazł Cerber, GLA-75 r1). Tu kształt
 * odpowiedzi jest sprawdzalny testem.
 */
export function assemblyFromResponse(body: unknown): AcroAssembly | null {
	if (!body || typeof body !== 'object' || Array.isArray(body)) {
		return null;
	}

	const candidate = body as Partial<AcroAssembly>;

	if (!Array.isArray(candidate.data) || !Array.isArray(candidate.missing_sku_pairs)) {
		return null;
	}

	return { data: candidate.data, missing_sku_pairs: candidate.missing_sku_pairs };
}

/**
 * Dlaczego ekran montażu nie może iść dalej. Rozróżnienie jest istotne dla protetyka: luka
 * w katalogu to inna sprawa niż padnięte połączenie, a wcześniej oba dawały ten sam komunikat
 * o niepełnym katalogu — z pustymi nawiasami, bo nie było czego wypisać (Rita, GLA-75 r1).
 */
export enum AssemblyBlocker {
	None = 'none',
	LoadFailed = 'load-failed',
	Incomplete = 'incomplete',
}

export function assemblyBlocker(
	assembly: AcroAssembly | null,
	loadFailed: boolean,
): AssemblyBlocker {
	if (loadFailed) {
		return AssemblyBlocker.LoadFailed;
	}

	return isAssemblyComplete(assembly) ? AssemblyBlocker.None : AssemblyBlocker.Incomplete;
}

/**
 * Typ amputacji, jaki ma trafić do FORMULARZA po wczytaniu zamówienia.
 *
 * Normalizacja musi być U ŹRÓDŁA, nie przy zapisie. Zamówienie Acro z zapisanym wcześniej
 * `transhumeral` (dane, które realnie powstały przed GLA-81) ma ukryte pole typu amputacji,
 * więc protetyk nie ma czym tego poprawić — a model formularza zostawał transhumeralny.
 * Skutki w tej samej sesji: karta pomiarowa prosiła o wymiary kikuta ramienia, pokazywał się
 * checkbox zaworu, a payload szedł z `shoulder_disarticulation` RAZEM z `valve_opening`.
 * Po normalizacji u źródła ilustracja, pola pomiarowe, zawór i payload zgadzają się same
 * (znaleźli Cerber i Rita niezależnie, GLA-81 r2).
 */
export function initialAmputationTypeForOrder(order?: OrderModel | null): AmputationType {
	if (isShoulderDisarticulationPath(order)) {
		return AmputationType.ShoulderDisarticulation;
	}

	return resolveAmputationType(order?.order_details?.amputation_type, order?.initial_amputation_type);
}

/**
 * Czy pokazać pole typu amputacji dla TEGO zamówienia.
 *
 * Istnieje po to, żeby widok nie wybierał między `isShoulderDisarticulationPath`
 * a `isShoulderDisarticulationOrder` — podmiana jednego na drugie w `computed` wracała
 * pułapką jednokierunkową i nie łapał jej żaden test (wskazała Rita, GLA-81 r2).
 */
export function showsAmputationTypeForOrder(order: OrderModel | null | undefined, isAbove: boolean): boolean {
	return showsAmputationTypeField(isAbove, isShoulderDisarticulationPath(order));
}
